<template>
  <div>
    <div class="main-content" v-if="worker">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  <h4 class="mb-0">
                    {{ worker.name }}
                    <span :class="badgeSituacao.classe">{{ badgeSituacao.texto }}</span>
                    <span v-if="seloRelogio" :class="seloRelogio.classe" :title="seloRelogio.titulo">
                      {{ seloRelogio.texto }}
                    </span>
                    <span v-if="badgeModo" :class="badgeModo.classe" :title="badgeModo.titulo">
                      {{ badgeModo.texto }}
                    </span>
                  </h4>
                  <small class="text-muted">
                    {{ $t("Central") }}: {{ worker.center_name || "-" }} ·
                    {{ $t("Priority") }}: {{ worker.priority }} ·
                    {{ $t("updated every") }} {{ intervaloSegundos }}s
                  </small>
                </div>
                <div>
                  <b-button size="sm" variant="secondary" @click="carregar">
                    {{ $t("Refresh") }}
                  </b-button>
                  <b-button size="sm" variant="secondary" class="ml-1" @click="verificarAgora">
                    {{ $t("Test now") }}
                  </b-button>
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="ml-1"
                    :disabled="duplicating"
                    @click="duplicate"
                  >
                    {{ $t("Duplicate") }}
                  </b-button>
                  <b-button
                    size="sm"
                    variant="primary"
                    class="ml-1"
                    @click="$router.push('/workers/edit/' + worker.id)"
                  >
                    {{ $t("Edit") }}
                  </b-button>
                  <b-button size="sm" variant="outline-secondary" class="ml-1" @click="$router.push('/workers')">
                    {{ $t("Back") }}
                  </b-button>
                </div>
              </div>

              <div v-if="avisoDegradado" class="alert alert-danger mt-2 mb-0">
                <strong>{{ $t("Writing through the API: degraded") }}.</strong>
                {{ $t("This worker is up but not writing straight to the database") }}.
              </div>

              <div v-if="avisoRelogio" class="alert alert-danger mt-2 mb-0">
                <strong>{{ $t("Machine clock") }}: {{ telemetria.clock_drift_seconds }}s.</strong>
                {{ $t("The tracking timestamp written by this worker may be shifted, which affects the monitoring screen and the active vehicle filter") }}.
                {{ $t("Fix it with timedatectl on the server") }}.
                <strong>{{ $t("Synchronization keeps working") }}.</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="verificacao">
        <div class="col-md-12">
          <div class="card" :class="{ 'border-primary': recemCriado }">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <h5 class="mb-2">
                  {{ $t("Verification") }}
                  <small v-if="recemCriado" class="text-primary">
                    — {{ $t("worker just created") }}
                  </small>
                </h5>
                <b-button size="sm" variant="outline-secondary" @click="verificarAgora">
                  {{ $t("Verify again") }}
                </b-button>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <p class="text-muted mb-1">{{ $t("Server") }} — {{ $t("immediate") }}</p>
                  <ul class="list-unstyled mb-0">
                    <li>
                      {{ icone(!!verificacao.server.center) }}
                      {{ $t("Central and units consistent") }} —
                      {{ verificacao.server.center || "-" }},
                      {{ verificacao.server.units }} {{ $t("units") }}
                    </li>
                    <li>
                      {{ icone(verificacao.server.plates > 0) }} {{ $t("Plates covered") }} —
                      {{ verificacao.server.plates }} {{ $t("vehicles") }}
                    </li>
                    <li v-if="moviasCredencial">
                      {{ icone(moviasCredencial.status === "ok") }} {{ $t("Movias credentials") }} —
                      {{ moviasCredencial.status }}
                      <small v-if="moviasCredencial.message" class="text-muted">
                        ({{ moviasCredencial.message }})
                      </small>
                    </li>
                    <li v-else class="text-muted">
                      — {{ $t("Movias credentials") }}:
                      {{ $t("not tested on this page load") }}
                    </li>
                  </ul>
                </div>

                <div class="col-md-6">
                  <p class="text-muted mb-1">{{ $t("Agent") }} — {{ $t("answers in seconds") }}</p>
                  <ul class="list-unstyled mb-0" v-if="verificacao.agent">
                    <li>
                      {{ icone(true) }} {{ $t("Worker picked up by the agent") }} —
                      {{ verificacao.agent.hostname || "-" }}
                    </li>
                    <li>{{ icone(verificacao.agent.db_reachable) }} {{ $t("Database reachable") }}</li>
                    <li>{{ icone(verificacao.agent.movias_reachable) }} {{ $t("Movias reachable") }}</li>
                    <li>
                      {{ icone(relogioDaVerificacaoOk) }} {{ $t("Machine clock") }} —
                      {{ verificacao.agent.clock_drift_seconds }}s
                      <span v-if="!relogioDaVerificacaoOk" class="text-danger">
                        ({{ $t("limit") }}: {{ margens.clock_drift_critical_seconds }}s)
                      </span>
                    </li>
                    <li v-if="verificacao.agent.last_error" class="text-danger">
                      {{ icone(false) }} {{ verificacao.agent.last_error }}
                    </li>
                  </ul>
                  <p class="mb-0" v-else>
                    <span v-if="esperandoAgente" class="text-muted">
                      {{ $t("Waiting for the agent to report") }}…
                    </span>
                    <span v-else class="text-muted">
                      {{ $t("The agent has not picked up this worker yet") }}.
                      {{ $t("Check the service on the server") }}:
                      <code>systemctl status frotas-worker</code>.
                      {{ $t("This is not a registration error") }}.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="card">
            <div class="card-body">
              <h5>{{ $t("Key") }}</h5>
              <p class="mb-1">
                <code>{{ worker.uuid }}</code>
              </p>
              <small class="text-muted">
                {{ $t("Used to filter this worker in the server log") }}:
                <code>journalctl -u frotas-worker | grep {{ prefixoLog }}</code>
              </small>
              <div class="mt-2">
                <b-button size="sm" variant="outline-secondary" @click="copiarChave">
                  {{ $t("Copy") }}
                </b-button>
                <b-button size="sm" variant="outline-danger" class="ml-1" @click="regerarChave">
                  {{ $t("Regenerate key") }}
                </b-button>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <h5>{{ $t("Role per unit") }}</h5>
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="unit in worker.units" :key="unit.id">
                    <td>
                      {{ unit.name }}
                      <small v-if="unit.center_mismatch" class="d-block text-danger">
                        {{ $t("Unit from another central: the agent ignores it") }}
                      </small>
                      <small
                        v-else-if="unit.role !== 'active' && unit.active_worker_name"
                        class="d-block text-muted"
                      >
                        {{ $t("Active on this unit") }}: {{ unit.active_worker_name }}
                      </small>
                    </td>
                    <td class="text-right">
                      <span v-if="unit.center_mismatch" class="badge badge-danger">
                        {{ $t("inconsistent") }}
                      </span>
                      <span
                        v-else
                        :class="unit.role === 'active' ? 'badge badge-success' : 'badge badge-secondary'"
                      >
                        {{ unit.role }}
                      </span>
                    </td>
                  </tr>
                  <tr v-if="!worker.units || !worker.units.length">
                    <td class="text-muted">{{ $t("No units") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <h5>{{ $t("Server identity") }}</h5>
              <table class="table table-sm mb-0" v-if="telemetria">
                <tbody>
                  <tr><td>hostname</td><td class="text-right">{{ ou(telemetria.hostname) }}</td></tr>
                  <tr>
                    <td>{{ $t("Local / public IP") }}</td>
                    <td class="text-right">{{ ou(telemetria.local_ip) }} / {{ ou(telemetria.public_ip) }}</td>
                  </tr>
                  <tr><td>MAC</td><td class="text-right">{{ ou(telemetria.mac_address) }}</td></tr>
                  <tr>
                    <td>{{ $t("OS") }} / timezone</td>
                    <td class="text-right">
                      {{ ou(telemetria.os_name) }} {{ ou(telemetria.os_version, "") }} ·
                      {{ ou(telemetria.timezone) }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t("Clock drift") }}</td>
                    <td class="text-right">
                      <span v-if="seloRelogio" :class="seloRelogio.classe">{{ seloRelogio.texto }}</span>
                      <span v-else class="badge badge-success">
                        {{ telemetria.clock_drift_seconds === null ? "-" : telemetria.clock_drift_seconds + "s" }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-muted mb-0">{{ $t("The agent has not reported yet") }}</p>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="card">
            <div class="card-body">
              <h5>{{ $t("Diagnosis") }}</h5>
              <table class="table table-sm mb-0" v-if="telemetria">
                <tbody>
                  <tr>
                    <td>{{ $t("Agent version") }} / Python</td>
                    <td class="text-right">
                      {{ ou(telemetria.agent_version) }} / {{ ou(telemetria.python_version) }}
                    </td>
                  </tr>
                  <tr>
                    <td>{{ $t("Process uptime") }}</td>
                    <td class="text-right">{{ duracao(telemetria.process_uptime_seconds) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Machine uptime") }}</td>
                    <td class="text-right">{{ duracao(telemetria.machine_uptime_seconds) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Consecutive errors") }}</td>
                    <td class="text-right">
                      <span :class="Number(telemetria.consecutive_errors) > 0 ? 'text-danger' : ''">
                        {{ ou(telemetria.consecutive_errors, "0") }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td>CPU / {{ $t("memory") }} / {{ $t("disk") }}</td>
                    <td class="text-right">
                      {{ percentual(telemetria.cpu_percent) }} ·
                      {{ percentual(telemetria.memory_percent) }} ·
                      {{ percentual(telemetria.disk_percent) }}
                    </td>
                  </tr>
                  <tr v-if="telemetria.last_error">
                    <td colspan="2" class="text-danger">
                      <small>{{ telemetria.last_error }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-muted mb-0">{{ $t("The agent has not reported yet") }}</p>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h5 class="mb-0">{{ $t("Telemetry history") }}</h5>
                <div class="btn-group btn-group-sm">
                  <button
                    v-for="opcao in janelas"
                    :key="opcao.dias"
                    type="button"
                    class="btn"
                    :class="janelaEmDias === opcao.dias ? 'btn-primary' : 'btn-outline-primary'"
                    @click="trocarJanela(opcao.dias)"
                  >
                    {{ opcao.rotulo }}
                  </button>
                </div>
              </div>

              <div v-if="carregandoHistorico" class="text-muted">{{ $t("Loading") }}...</div>

              <div
                v-else-if="historico.samples && historico.samples.length"
                class="grafico-telemetria"
              >
                <grafico-telemetria :series="historico.samples" :styles="estiloGrafico" />
              </div>

              <p v-else class="text-muted mb-0">{{ $t("No telemetry recorded in this window") }}</p>

              <table v-if="temResumo" class="table table-sm mt-3 mb-0 resumo-telemetria">
                <thead>
                  <tr>
                    <th></th>
                    <th class="text-right">{{ $t("Highest") }}</th>
                    <th class="text-right">{{ $t("Average") }}</th>
                    <th class="text-right">{{ $t("Lowest") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in resumoLinhas" :key="item.chave">
                    <td>
                      <span class="marca-serie" :style="{ backgroundColor: item.cor }"></span>
                      {{ item.rotulo }}
                    </td>
                    <td class="text-right">{{ item.max }}%</td>
                    <td class="text-right">{{ item.avg }}%</td>
                    <td class="text-right">{{ item.min }}%</td>
                  </tr>
                </tbody>
              </table>

              <p
                v-if="historico.offline_periods && historico.offline_periods.length"
                class="text-warning mt-3 mb-0"
              >
                <small>
                  {{ $t("Offline in this window") }}: {{ tempoOffline() }}
                  ({{ historico.offline_periods.length }}x)
                </small>
              </p>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <h5>{{ $t("Last cycle") }}</h5>
              <table class="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td>{{ $t("Last heartbeat") }}</td>
                    <td class="text-right">{{ desde(worker.last_heartbeat_at) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Last synchronization") }}</td>
                    <td class="text-right">{{ desde(worker.last_sync_at) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Changed rows") }}</td>
                    <td class="text-right">{{ ou(telemetria && telemetria.last_cycle_records) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Duration") }}</td>
                    <td class="text-right">{{ milissegundos(telemetria && telemetria.last_cycle_duration_ms) }}</td>
                  </tr>
                  <tr>
                    <td>{{ $t("Movias / database latency") }}</td>
                    <td class="text-right">
                      {{ milissegundos(telemetria && telemetria.movias_latency_ms) }} ·
                      {{ milissegundos(telemetria && telemetria.db_latency_ms) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <small class="text-muted d-block mt-2">
                {{ $t("Changed rows out of the fleet read: a low number on a still yard is normal") }}.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="main-content">
      <p class="text-muted">{{ $t("Loading") }}…</p>
    </div>
  </div>
</template>

<script>
import { BButton } from "bootstrap-vue";
import GraficoTelemetria, { CORES_SERIE } from "./GraficoTelemetria.vue";

const INTERVALO_MS = 30000;
const INTERVALO_VERIFICACAO_MS = 2000;
const TENTATIVAS_MAXIMAS = 45;

export default {
  name: "workerView",
  components: {
    BButton,
    GraficoTelemetria,
  },
  data() {
    return {
      worker: null,
      telemetria: null,
      margens: {
        clock_drift_warning_seconds: 5,
        clock_drift_critical_seconds: 30,
      },
      timer: null,
      verificacao: null,
      moviasCredencial: null,
      esperandoAgente: false,
      duplicating: false,
      tentativas: 0,
      timerVerificacao: null,
      historico: { samples: [], offline_periods: [], bucket_seconds: 0 },
      janelaEmDias: 1,
      carregandoHistorico: false,
      historicoCarregado: false,
      janelas: [
        { dias: 1, rotulo: "24h" },
        { dias: 7, rotulo: "7d" },
        { dias: 30, rotulo: "30d" },
      ],
    };
  },
  computed: {
    intervaloSegundos() {
      return INTERVALO_MS / 1000;
    },
    estiloGrafico() {
      return { position: "relative", height: "260px", width: "100%" };
    },
    temResumo() {
      return Boolean(this.historico.summary && this.historico.summary.cpu);
    },
    resumoLinhas() {
      const resumo = this.historico.summary || {};

      return [
        { chave: "cpu", rotulo: "CPU", cor: CORES_SERIE.cpu },
        { chave: "memory", rotulo: this.$t("Memory"), cor: CORES_SERIE.memory },
        { chave: "disk", rotulo: this.$t("Disk"), cor: CORES_SERIE.disk },
      ]
        .filter((item) => resumo[item.chave])
        .map((item) => ({ ...item, ...resumo[item.chave] }));
    },
    prefixoLog() {
      return this.worker && this.worker.uuid ? this.worker.uuid.substring(0, 8) : "";
    },
    estaOnline() {
      if (!this.worker || !this.worker.status || !this.worker.last_heartbeat_at) {
        return false;
      }

      const tolerancia = Number(this.worker.offline_threshold_seconds || 180);
      const silencio = (Date.now() - new Date(this.worker.last_heartbeat_at).getTime()) / 1000;

      return silencio <= tolerancia;
    },
    badgeSituacao() {
      if (!this.worker.status) {
        return { classe: "badge badge-secondary", texto: this.$t("Inactive") };
      }

      return this.estaOnline
        ? { classe: "badge badge-success", texto: "Online" }
        : { classe: "badge badge-danger", texto: "Offline" };
    },
    badgeModo() {
      if (!this.estaOnline || !this.telemetria || !this.telemetria.write_mode) {
        return null;
      }

      if (this.telemetria.write_mode === "direct") {
        return { classe: "badge badge-success", texto: this.$t("Direct"), titulo: null };
      }

      return {
        classe: "badge badge-danger",
        texto: this.$t("Contingency"),
        titulo: this.$t("Writing through the API: degraded"),
      };
    },
    desvioAbsoluto() {
      if (!this.telemetria || this.telemetria.clock_drift_seconds === null) {
        return null;
      }

      return Math.abs(Number(this.telemetria.clock_drift_seconds));
    },
    seloRelogio() {
      if (this.desvioAbsoluto === null) {
        return null;
      }

      if (this.desvioAbsoluto < Number(this.margens.clock_drift_warning_seconds)) {
        return null;
      }

      const critico = this.desvioAbsoluto >= Number(this.margens.clock_drift_critical_seconds);
      const sinal = Number(this.telemetria.clock_drift_seconds) > 0 ? "+" : "-";

      return {
        classe: critico ? "badge badge-danger" : "badge badge-warning",
        texto: "⏱ " + sinal + this.desvioAbsoluto + "s",
        titulo: this.$t(
          "Clock drift: the tracking timestamp written by this worker may be shifted"
        ),
      };
    },
    avisoRelogio() {
      return (
        this.desvioAbsoluto !== null &&
        this.desvioAbsoluto >= Number(this.margens.clock_drift_critical_seconds)
      );
    },
    avisoDegradado() {
      return this.estaOnline && this.telemetria && this.telemetria.write_mode === "fallback";
    },
    recemCriado() {
      return this.$route.query.novo === "1";
    },
    relogioDaVerificacaoOk() {
      if (!this.verificacao || !this.verificacao.agent) {
        return true;
      }

      const desvio = this.verificacao.agent.clock_drift_seconds;

      if (desvio === null || desvio === undefined) {
        return true;
      }

      return Math.abs(Number(desvio)) < Number(this.margens.clock_drift_critical_seconds);
    },
  },
  mounted() {
    this.getMargens();
    this.carregar();
    this.buscarVerificacao(true);

    this.timer = setInterval(this.carregar, INTERVALO_MS);
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    this.pararPollingVerificacao();
  },
  methods: {
    ou(valor, padrao = "-") {
      return valor === null || valor === undefined || valor === "" ? padrao : valor;
    },
    percentual(valor) {
      return valor === null || valor === undefined ? "-" : Number(valor).toFixed(1) + "%";
    },
    milissegundos(valor) {
      if (valor === null || valor === undefined) {
        return "-";
      }

      return Number(valor) >= 1000
        ? (Number(valor) / 1000).toFixed(2).replace(".", ",") + " s"
        : valor + " ms";
    },
    duracao(segundos) {
      if (segundos === null || segundos === undefined) {
        return "-";
      }

      const total = Number(segundos);
      const dias = Math.floor(total / 86400);
      const horas = Math.floor((total % 86400) / 3600);

      if (dias > 0) {
        return dias + " d " + horas + " h";
      }

      const minutos = Math.floor((total % 3600) / 60);

      return horas > 0 ? horas + " h " + minutos + " min" : minutos + " min";
    },
    desde(valor) {
      if (!valor) {
        return "-";
      }

      const segundos = Math.floor((Date.now() - new Date(valor).getTime()) / 1000);

      if (segundos < 60) {
        return this.$t("ago") + " " + segundos + " s";
      }

      const minutos = Math.floor(segundos / 60);

      if (minutos < 60) {
        return this.$t("ago") + " " + minutos + " min";
      }

      return new Date(valor).toLocaleString();
    },
    getMargens() {
      axios
        .get(this.$store.state.api + "workers/form/defaults")
        .then((response) => {
          this.margens = { ...this.margens, ...response.data };
        })
        .catch(() => {});
    },
    carregar() {
      const id = this.$route.params.id;

      if (!id) {
        return;
      }

      axios
        .get(this.$store.state.api + "workers/" + id + "?with[]=center&with[]=units&with[]=telemetry")
        .then((response) => {
          const dados = response.data.data ? response.data.data[0] : response.data;

          this.worker = {
            id: dados.id,
            uuid: dados.uuid,
            name: dados.name,
            status: Boolean(dados.status),
            priority: dados.priority,
            offline_threshold_seconds: dados.offline_threshold_seconds,
            last_heartbeat_at: dados.last_heartbeat_at,
            last_sync_at: dados.last_sync_at,
            center_name: dados.center ? dados.center.name : dados.center_name,
            units: dados.units || [],
          };

          this.telemetria = dados.telemetry || null;

          this.carregarPapeis(id);

          if (!this.historicoCarregado) {
            this.carregarHistorico();
          }
        })
        .catch((error) => this.$error(error));
    },
    carregarPapeis(id) {
      axios
        .get(this.$store.state.api + "workers/" + id + "/roles")
        .then((response) => {
          const papeis = {};

          (response.data.units || []).forEach((unidade) => {
            papeis[unidade.id] = unidade.role;
          });

          this.worker.units = this.worker.units.map((unidade) => ({
            ...unidade,
            role: papeis[unidade.id] || unidade.role,
          }));
        })
        .catch(() => {});
    },
    carregarHistorico() {
      if (!this.worker || !this.worker.id) {
        return;
      }

      const ate = new Date();
      const de = new Date(ate.getTime() - this.janelaEmDias * 86400000);

      this.carregandoHistorico = true;

      axios
        .get(this.$store.state.api + "workers/" + this.worker.id + "/telemetry-history", {
          params: { from: de.toISOString(), to: ate.toISOString() },
        })
        .then(({ data }) => {
          this.historico = data;
          this.historicoCarregado = true;
        })
        .finally(() => {
          this.carregandoHistorico = false;
        });
    },
    trocarJanela(dias) {
      this.janelaEmDias = dias;
      this.carregarHistorico();
    },
    tempoOffline() {
      const total = (this.historico.offline_periods || []).reduce(
        (soma, periodo) => soma + Number(periodo.segundos || 0),
        0
      );

      return this.duracao(total);
    },
    icone(ok) {
      return ok ? "✓" : "✗";
    },
    buscarVerificacao(iniciarPolling = false) {
      const id = this.$route.params.id;

      if (!id) {
        return;
      }

      axios
        .get(this.$store.state.api + "workers/" + id + "/check")
        .then((response) => {
          this.verificacao = response.data;

          if (response.data.status === "done") {
            this.pararPollingVerificacao();
            this.esperandoAgente = false;
            return;
          }

          if (iniciarPolling) {
            this.iniciarPollingVerificacao(id);
          }
        })
        .catch(() => {});
    },
    iniciarPollingVerificacao(id) {
      this.pararPollingVerificacao();
      this.tentativas = 0;
      this.esperandoAgente = true;

      this.timerVerificacao = setInterval(() => {
        this.tentativas += 1;

        if (this.tentativas > TENTATIVAS_MAXIMAS) {
          this.pararPollingVerificacao();
          this.esperandoAgente = false;
          return;
        }

        axios
          .get(this.$store.state.api + "workers/" + id + "/check")
          .then((response) => {
            this.verificacao = response.data;

            if (response.data.status === "done") {
              this.pararPollingVerificacao();
              this.esperandoAgente = false;
            }
          })
          .catch(() => {
            this.pararPollingVerificacao();
            this.esperandoAgente = false;
          });
      }, INTERVALO_VERIFICACAO_MS);
    },
    pararPollingVerificacao() {
      if (this.timerVerificacao) {
        clearInterval(this.timerVerificacao);
        this.timerVerificacao = null;
      }
    },
    verificarAgora() {
      this.moviasCredencial = null;

      axios
        .post(this.$store.state.api + "workers/" + this.worker.id + "/check")
        .then((response) => {
          if (response.data.check) {
            this.moviasCredencial = response.data.check.movias_credentials;
          }

          this.$message(
            this.$t("Verification"),
            this.$t("Requested: the agent answers in seconds"),
            "success"
          );

          this.buscarVerificacao(true);
        })
        .catch((error) => this.$error(error));
    },
    copiarChave() {
      const area = document.createElement("textarea");
      area.value = this.worker.uuid;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      document.body.removeChild(area);

      this.$message(this.$t("Key"), this.$t("Copied"), "success");
    },
    duplicate() {
      this.duplicating = true;

      axios
        .post(this.$store.state.api + "workers/" + this.worker.id + "/duplicate")
        .then((response) => {
          this.$message(
            this.$t("Duplicate"),
            this.$t("Copy created with its own key and the next priority. Adjust and save."),
            "success"
          );

          this.$router.push("/workers/edit/" + response.data.worker.id);
        })
        .catch((error) => this.$error(error))
        .finally(() => {
          this.duplicating = false;
        });
    },
    regerarChave() {
      axios
        .post(this.$store.state.api + "workers/" + this.worker.id + "/regenerate-key")
        .then((response) => {
          this.worker.uuid = response.data.uuid;
          this.$message(
            this.$t("Key"),
            this.$t("New key generated: the agent picks it up on its own"),
            "success"
          );
        })
        .catch((error) => this.$error(error));
    },
  },
};
</script>

<style scoped>
.grafico-telemetria {
  position: relative;
  height: 260px;
  width: 100%;
}

.resumo-telemetria th {
  border-top: 0;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b6b66;
}

.resumo-telemetria td {
  vertical-align: middle;
}

.marca-serie {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
</style>
